import { Injectable, signal } from "@angular/core";

/** Minimal catalog entry shared by both setup screens. */
export interface CatalogChoice {
  readonly id: string;
  readonly name: string;
  readonly url?: string;
}

interface TerrainIndexJson {
  readonly terrains: CatalogChoice[];
}

interface BoatIndexJson {
  readonly boats: CatalogChoice[];
}

/**
 * Loads (and caches across the app lifetime) the static terrain/boat
 * index JSONs that drive both Game Setup and Practice Setup screens.
 * Without this each component re-fetches on every open.
 */
@Injectable({ providedIn: "root" })
export class CatalogService {
  private terrainPromise: Promise<readonly CatalogChoice[]> | null = null;
  private boatPromise: Promise<readonly CatalogChoice[]> | null = null;

  readonly terrains = signal<readonly CatalogChoice[]>([]);
  readonly boats = signal<readonly CatalogChoice[]>([]);

  loadTerrains(): Promise<readonly CatalogChoice[]> {
    if (!this.terrainPromise) {
      this.terrainPromise = fetchJson<TerrainIndexJson>("terrain/index.json")
        .then((j) => {
          const list = Array.isArray(j.terrains) ? j.terrains : [];
          this.terrains.set(list);
          return list;
        })
        .catch((err) => {
          this.terrainPromise = null;
          throw err;
        });
    }
    return this.terrainPromise;
  }

  loadBoats(): Promise<readonly CatalogChoice[]> {
    if (!this.boatPromise) {
      this.boatPromise = fetchJson<BoatIndexJson>("boats/index.json")
        .then((j) => {
          const list = Array.isArray(j.boats) ? j.boats : [];
          this.boats.set(list);
          return list;
        })
        .catch((err) => {
          this.boatPromise = null;
          throw err;
        });
    }
    return this.boatPromise;
  }

  loadAll(): Promise<[readonly CatalogChoice[], readonly CatalogChoice[]]> {
    return Promise.all([this.loadTerrains(), this.loadBoats()]);
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) throw new Error(`fetch ${url} -> HTTP ${response.status}`);
  return (await response.json()) as T;
}
